import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ME_KEY } from '@/shared/api/queryClient'
import { ApiError } from '@/shared/api/ApiError'
import { meApi } from '@/features/auth/api/meApi'
import type { PermissionCode } from '@/features/auth/types'

export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: meApi.get,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  })
}

/** Permissions from `/api/me`. Fails closed: loading, error or inactive → `can()` is false. */
export function useAuthz() {
  const { data, isLoading, isError, error, refetch, dataUpdatedAt } = useMe()

  const allowed = useMemo(
    () => new Set<PermissionCode>(data?.isActive ? data.permissions : []),
    [data],
  )
  const can = useCallback((p: PermissionCode) => allowed.has(p), [allowed])

  return {
    me: data,
    isLoading,
    isError,
    error,
    noAccess: error instanceof ApiError && error.code === 'NO_ACCESS',
    can,
    roles: data?.roles ?? [],
    refetch,
    dataUpdatedAt,
  }
}
